import { uploadForm } from '@/api/api-client';
import { sendChatMessage, sendChatVoice } from '@/api/fbapi';
import { useChat } from '@/contexts/ChatContext';
import { useToast } from '@/hooks/use-toast';
import { FBMessage } from '@/types/fb-type';
import { getPageId } from '@/services/utl-service';
import EmojiPicker, { EmojiClickData, Theme } from 'emoji-picker-react';
import imageCompression from 'browser-image-compression';
import { MessageSquare, Paperclip, Send, Smile } from 'lucide-react';
import { FormEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import LiveCommentReplyFallback from './LiveCommentReplyFallback';
import { AudioRecorder, useAudioRecorder } from 'react-audio-voice-recorder';

interface ChatInputProps {
  conversationId: string;
  userId: string;
  /** Display name of the customer — used in the live-comment fallback banner */
  userName?: string;
  messagingWindow?: 'STANDARD' | 'HUMAN_AGENT' | 'CLOSED' | 'UNKNOWN';
  onSendMessage: (success: boolean, conversationd: string, message?: FBMessage) => void;
}
type UploadItem = {
  file: File;
  preview: string;
  progress: number;
  uploading: boolean;
  error?: string | null;
  controller?: AbortController | null;
};

const IOS_IMAGE_EXT_RE = /\.(heic|heif|jpg|jpeg|png|webp)$/i;
const MOBILE_IMAGE_MAX_SIZE = 1600;
const IMAGE_NORMALIZE_THRESHOLD_BYTES = 1 * 1024 * 1024; // 1 MB — keeps iPhone camera shots under upload limit
const IMAGE_JPEG_QUALITY = 0.82;


/**
 * Heuristic: treat a send error as a "Messenger not available" failure when the
 * response message contains keywords that Facebook typically returns when a user
 * hasn't opened a DM thread or the 24-hr messaging window has expired.
 */
function isMessengerUnavailableError(msg: string): boolean {
  const lower = msg.toLowerCase();
  return (
    lower.includes('cannot send') ||
    lower.includes('messaging') ||
    lower.includes('window') ||
    lower.includes('permission') ||
    lower.includes('not eligible') ||
    lower.includes('access token') ||
    lower.includes('190') ||
    lower.includes('551') ||
    lower.includes('200')
  );
}

function getAudioFileExtension(blob: Blob) {
  const mimeSubtype = blob.type
    .split(';')[0]
    .split('/')[1]
    ?.trim()
    .toLowerCase()
    .replace(/^x-/, '');

  return mimeSubtype || 'webm';
}

function getVoiceUnsupportedMessage() {
  if (typeof window === 'undefined') return 'Voice recording is not available here.';
  if (!window.isSecureContext) {
    return 'Voice recording needs HTTPS on iPhone/iPad and most browsers. Please open the app with HTTPS.';
  }
  if (!navigator.mediaDevices?.getUserMedia) {
    return 'This browser does not allow microphone recording.';
  }
  if (typeof MediaRecorder === 'undefined') {
    return 'This browser does not support audio recording.';
  }
  return null;
}

function isImageFile(file: File) {
  return file.type.startsWith('image/') || IOS_IMAGE_EXT_RE.test(file.name);
}

function isConvertibleImage(file: File) {
  const type = file.type.toLowerCase();
  // iOS Files app often returns type="" — treat empty type as convertible if extension matches
  const hasEmptyOrUnknownType = !type || type === 'application/octet-stream';
  return (
    type !== 'image/gif' &&
    type !== 'image/svg+xml' &&
    (hasEmptyOrUnknownType ||
      file.size > IMAGE_NORMALIZE_THRESHOLD_BYTES ||
      type.includes('heic') ||
      type.includes('heif') ||
      /\.(heic|heif)$/i.test(file.name))
  );
}



async function normalizeImageForUpload(file: File): Promise<File> {
  if (!isConvertibleImage(file)) return file;

  try {
    const options = {
      maxSizeMB: IMAGE_NORMALIZE_THRESHOLD_BYTES / (1024 * 1024),
      maxWidthOrHeight: MOBILE_IMAGE_MAX_SIZE,
      useWebWorker: true,
      fileType: 'image/jpeg',
      initialQuality: IMAGE_JPEG_QUALITY,
    };
    
    const compressedBlob = await imageCompression(file, options);
    const baseName = file.name.replace(/\.[^.]+$/, '') || 'photo';
    
    return new File([compressedBlob], `${baseName}.jpg`, {
      type: 'image/jpeg',
      lastModified: file.lastModified,
    });
  } catch (error) {
    console.error('Image compression failed:', error);
    return file;
  }
}

export default function ChatInput({
  conversationId,
  userId,
  userName = 'this user',
  messagingWindow = 'UNKNOWN',
  onSendMessage,
}: ChatInputProps) {
  const { currentConversation, reloadMessages, refreshMessages } = useChat();
  const inputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showQuickMessages, setShowQuickMessages] = useState(false);
  /** True when a Messenger send failure has been detected → show comment-reply fallback */
  const [showLiveCommentFallback, setShowLiveCommentFallback] = useState(false);
  // Predefined quick messages
  const quickMessages = [
    { id: 1, text: '🥰 អរគុណច្រើនបង ហេងហេង 😍 🙏🙏', icon: '🙏' },
    { id: 2, text: '📦 អធ្យាស្រ័យបង ជួយវេលុយអោយប្អូនផងបង', icon: '💰' },
    { id: 3, text: '💵 បងជួយឆែកតម្លៃផងបង', icon: '🔍' },
    { id: 4, text: '🚚 អធ្យាស្រ័យបងសូម ទីតាំងសម្រាប់ដឹកជញ្ជូនផងបង', icon: '✅' },
    { id: 5, text: '📸 បងសូមជួយផ្ញើររូបអោយប្អុនបន្ថែមទៀត', icon: '📷' },
    { id: 6, text: '✨ អរគុណសម្រាប់ការកម្មង់បង', icon: '🙏' },
    { id: 7, text: '⏰ អធ្យាស្រ័យបងសូមរង់ចាំបន្តិចបង', icon: '⌛' },
  ];
  const handleEmojiClick = (emojiData: EmojiClickData) => {
    setMessage((prev) => prev + emojiData.emoji);
    setShowEmojiPicker(false);
    inputRef.current?.focus();
  };
  const { toast } = useToast();
  const [dragging, setDragging] = useState(false);
  const [items, setItems] = useState<UploadItem[]>([]);
  const replyClosed = messagingWindow === 'CLOSED' || messagingWindow === 'UNKNOWN';

  //  console.log('conversation id', conversationId, 'userid', userId);

  const recorderControls = useAudioRecorder(
    {
      echoCancellation: true,
      noiseSuppression: true,
      autoGainControl: true,
    },
    (exception) => {
      toast({
        title: 'Microphone unavailable',
        description:
          exception.name === 'NotAllowedError'
            ? 'Please allow microphone access in your browser settings.'
            : exception.message || 'Unable to start voice recording.',
        variant: 'destructive',
      });
    },
  );

  const sendVoiceBlob = useCallback(async (blob: Blob) => {
    const ext = getAudioFileExtension(blob);
    const localAudioUrl = URL.createObjectURL(blob);
    const file = new File([blob], `voice-${Date.now()}.${ext}`, {
      type: blob.type,
    });
    const form = new FormData();
    form.append('audio', file);
    form.append('recipientId', userId);
    form.append('conversationId', conversationId);
    setIsSubmitting(true);
    try {
      const voiceResponse = await sendChatVoice(userId, form);
      if (voiceResponse.success) {
        const sentMessage = new FBMessage();
        if (voiceResponse.data) {
          Object.assign(sentMessage, voiceResponse.data);
        } else {
          sentMessage.id = `temp-voice-${Date.now()}`;
          sentMessage.createdTime = new Date().toISOString();
        }

        if (!sentMessage.from || !sentMessage.from.id) {
          sentMessage.from = { id: getPageId() || '', name: 'Page', username: '' };
        }

        sentMessage.attachments = [
          {
            id: voiceResponse.data?.attachments?.[0]?.id || `att-voice-${Date.now()}`,
            mimeType: blob.type || 'audio/webm',
            audio: true,
            size: blob.size,
            fileUrl: localAudioUrl,
            image: false,
            video: false,
          },
        ];

        onSendMessage(true, conversationId, sentMessage);
        // Refresh gently in background so server data seamlessly syncs without wiping preview
        setTimeout(() => refreshMessages(), 1500);
        setTimeout(() => refreshMessages(), 4000);
      } else {
        toast({
          title: 'Error',
          description: voiceResponse.message || 'Unexpected error on sending voice.',
          variant: 'destructive',
        });
      }
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error?.message || 'Unexpected error on sending voice.',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  }, [conversationId, onSendMessage, toast, userId, refreshMessages]);

  const guardedRecorderControls = useMemo(() => ({
    ...recorderControls,
    startRecording: () => {
      const unsupportedMessage = getVoiceUnsupportedMessage();
      if (unsupportedMessage) {
        toast({
          title: 'Voice recording unavailable',
          description: unsupportedMessage,
          variant: 'destructive',
        });
        return;
      }

      try {
        recorderControls.startRecording();
      } catch (error: any) {
        toast({
          title: 'Microphone unavailable',
          description: error?.message || 'Unable to start voice recording.',
          variant: 'destructive',
        });
      }
    },
  }), [recorderControls, toast]);

  //const { sendMessage } = useChat();
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const picker = document.getElementById('emoji-picker-box');
      const button = document.getElementById('emoji-btn');
      const quickMsgPicker = document.getElementById('quick-msg-picker');
      const quickMsgButton = document.getElementById('quick-msg-btn');

      if (
        picker &&
        button &&
        !picker.contains(e.target as Node) &&
        !button.contains(e.target as Node)
      ) {
        setShowEmojiPicker(false);
      }

      if (
        quickMsgPicker &&
        quickMsgButton &&
        !quickMsgPicker.contains(e.target as Node) &&
        !quickMsgButton.contains(e.target as Node)
      ) {
        setShowQuickMessages(false);
      }
    };
    if (showEmojiPicker || showQuickMessages) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showEmojiPicker, showQuickMessages]);

  // Auto-stop recording at 5 minutes to prevent excessively large files while allowing long voice notes
  const MAX_RECORDING_SECONDS = 300;
  useEffect(() => {
    if (!recorderControls.isRecording) return;
    if (recorderControls.recordingTime === MAX_RECORDING_SECONDS - 15) {
      toast({ title: 'Voice recording', description: '15 seconds remaining (5 min limit).', duration: 4000 });
    }
    if (recorderControls.recordingTime >= MAX_RECORDING_SECONDS) {
      recorderControls.stopRecording();
    }
  }, [recorderControls.isRecording, recorderControls.recordingTime, recorderControls.stopRecording, toast]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (!message.trim() || replyClosed) return;

    setIsSubmitting(true);
    try {
      const sendChatResponse = await sendChatMessage(userId, message);
      if (sendChatResponse.success) {
        onSendMessage(true, conversationId, sendChatResponse.data);
        setMessage('');
        // Clear fallback if a previous send had triggered it
        setShowLiveCommentFallback(false);
      } else {
        var msg = sendChatResponse.message || 'Unexpected error';
        console.log('err', sendChatResponse);
        // Show comment-reply fallback when the Messenger API rejects the message
        // (e.g. user has no DM thread — they only commented on a FB Live)
        if (isMessengerUnavailableError(msg)) {
          setShowLiveCommentFallback(true);
        } else {
          toast({ title: 'Error', description: msg, variant: 'destructive' });
        }
      }
    } catch (error) {
      console.error('Error sending message:', error);
    } finally {
      setIsSubmitting(false);
      inputRef.current?.focus();
      inputRef.current?.click();
    }
  };

  const handleQuickMessageClick = async (quickMsg: string) => {
    if (replyClosed) return;
    setShowQuickMessages(false);
    setIsSubmitting(true);
    try {
      const sendChatResponse = await sendChatMessage(userId, quickMsg);
      if (sendChatResponse.success) {
        onSendMessage(true, conversationId, sendChatResponse.data);
        setShowLiveCommentFallback(false);
      } else {
        var msg = sendChatResponse.message || 'Unexpected error';
        if (isMessengerUnavailableError(msg)) {
          setShowLiveCommentFallback(true);
        } else {
          toast({ title: 'Error', description: msg, variant: 'destructive' });
        }
      }
    } catch (error) {
      console.error('Error sending quick message:', error);
    } finally {
      setIsSubmitting(false);
      inputRef.current?.focus();
    }
  };

  // cleanup blob URLs
  useEffect(() => {
    return () => items.forEach((i) => URL.revokeObjectURL(i.preview));
  }, [items]);

  // drop handler (images only, immediate upload)
  const handleDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragging(false);
    const files = Array.from(e.dataTransfer.files || []);
    if (!files.length) return;

    const accepted: UploadItem[] = [];
    for (const f of files) {
      if (!isImageFile(f)) continue;
      accepted.push({
        file: f,
        preview: URL.createObjectURL(f),
        progress: 0,
        uploading: false,
        error: null,
        controller: null,
      });
    }
    if (!accepted.length) return;

    setItems((prev) => [...prev, ...accepted]);
    // auto-start upload for each
    requestAnimationFrame(() => accepted.forEach((it) => startUpload(it.file)));
  }, []);

  const startUpload = useCallback(
    async (file: File) => {
      const controller = new AbortController();
      setItems((prev) =>
        prev.map((it) =>
          it.file === file
            ? { ...it, uploading: true, error: null, progress: 0, controller }
            : it,
        ),
      );
      const uploadFile = await normalizeImageForUpload(file);
      if (controller.signal.aborted) return;
      const form = new FormData();
      form.append('image', uploadFile);
      form.append('recipientId', userId);
      form.append('conversationId', conversationId);
      setIsSubmitting(true);
      try {
        const res = await uploadForm<any>(`/fb/conversations/${userId}/image`, form, {
          signal: controller.signal,
          onProgress: (evt) => {
            if (!evt.total) return;
            const pct = Math.round((evt.loaded / evt.total) * 100);
            setItems((prev) =>
              prev.map((it) => (it.file === file ? { ...it, progress: pct } : it)),
            );
          },
          config: {
            withCredentials: true,
            timeout: 180000,
          },
        });
        if (controller.signal.aborted) return;

        if (res.success) {
          let previewUrl = '';
          setItems((prev) => {
            const uploaded = prev.find((it) => it.file === file);
            previewUrl = uploaded?.preview || '';
            // Retain preview URL so image stays rendered in chat bubble
            return prev.filter((it) => it.file !== file);
          });

          // Show the bubble immediately from the API response with local preview
          const sentMessage = new FBMessage();
          if (res.data) {
            Object.assign(sentMessage, res.data);
          } else {
            sentMessage.id = `temp-img-${Date.now()}`;
            sentMessage.createdTime = new Date().toISOString();
          }

          if (!sentMessage.from || !sentMessage.from.id) {
            sentMessage.from = { id: getPageId() || '', name: 'Page', username: '' };
          }

          sentMessage.attachments = [
            {
              id: res.data?.attachments?.[0]?.id || `att-${Date.now()}`,
              mimeType: file.type || 'image/jpeg',
              image: true,
              size: file.size,
              fileUrl: previewUrl,
              video: false,
              audio: false,
              imageData: {
                url: previewUrl,
                previewUrl: previewUrl,
                width: 0,
                height: 0,
                maxWidth: 0,
                maxHeight: 0,
                renderAsSticker: false,
              },
            },
          ];

          onSendMessage(true, conversationId, sentMessage);
          // Gently refresh in background so server data syncs without wiping preview
          setTimeout(() => refreshMessages(), 1500);
          setTimeout(() => refreshMessages(), 4000);
        } else {
          const canceled = res.status === 499 || /cancel/i.test(res.message || '');
          setItems((prev) =>
            prev.map((it) =>
              it.file === file
                ? {
                  ...it,
                  uploading: false,
                  progress: canceled ? it.progress : 0,
                  error: canceled ? 'Upload canceled.' : res.message || 'Upload failed.',
                  controller: null,
                }
                : it,
            ),
          );
        }
      } catch (err) {
        console.log('uploading image error', err);
        if (controller.signal.aborted) return;
        setItems((prev) =>
          prev.map((it) =>
            it.file === file
              ? { ...it, uploading: false, error: 'Upload failed.', controller: null }
              : it,
          ),
        );
      } finally {
        setIsSubmitting(false);
      }
    },
    [conversationId, onSendMessage, userId, refreshMessages],
  );

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const accepted: UploadItem[] = [];
    for (const f of files) {
      if (!isImageFile(f)) continue;
      accepted.push({
        file: f,
        preview: URL.createObjectURL(f),
        progress: 0,
        uploading: false,
        error: null,
        controller: null,
      });
    }
    if (!accepted.length) return;
    setItems((prev) => [...prev, ...accepted]);
    requestAnimationFrame(() => accepted.forEach((it) => startUpload(it.file)));
    e.target.value = '';
  };

  const cancelUpload = useCallback(
    (file: File) => {
      const it = items.find((i) => i.file === file);
      it?.controller?.abort();
      setItems((prev) =>
        prev.map((item) =>
          item.file === file
            ? { ...item, uploading: false, error: 'Upload canceled.', controller: null }
            : item,
        ),
      );
    },
    [items],
  );

  const removeItem = useCallback((file: File) => {
    setItems((prev) => {
      const found = prev.find((i) => i.file === file);
      if (found) URL.revokeObjectURL(found.preview);
      return prev.filter((i) => i.file !== file);
    });
  }, []);

  return (
    <>
      {messagingWindow === 'HUMAN_AGENT' && (
        <div className="border-b border-amber-200 bg-amber-50 px-4 py-2 text-xs text-amber-900">
          Human agent window: only a real support agent may send a manual reply, up to 7 days after the customer's message.
        </div>
      )}
      {replyClosed && (
        <div className="border-b border-gray-200 bg-gray-50 px-4 py-2 text-xs text-gray-700">
          Messaging window closed. Wait for the customer to send a new message before replying.
        </div>
      )}
      {/* ── FB Live comment-reply fallback banner ── */}
      {showLiveCommentFallback && (
        <LiveCommentReplyFallback
          userId={userId}
          userName={userName}
          onDismiss={() => setShowLiveCommentFallback(false)}
        />
      )}

      <div
        className={`relative ${dragging ? 'bg-blue-50' : 'bg-white'} px-2 sm:px-4 pt-2 pb-[calc(0.5rem+env(safe-area-inset-bottom,0px))] transition-colors`}
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragEnter={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={(e) => {
          if (e.currentTarget === e.target) setDragging(false);
        }}
        onDrop={handleDrop}
      >
        {/* DND Overlay */}
        {dragging && (
          <div className="absolute inset-0 z-40 pointer-events-none border-2 border-dashed border-blue-400/70 rounded-xl bg-blue-50/40 backdrop-blur-sm" />
        )}

        {/* Emoji Picker */}
        {showEmojiPicker && (
          <div
            id="emoji-picker-box"
            className="absolute bottom-16 left-3 z-50 bg-white border border-gray-200 rounded-xl shadow-lg w-[320px] max-w-[calc(100vw-24px)]"
          >
            <EmojiPicker
              onEmojiClick={handleEmojiClick}
              theme={Theme.AUTO}
              lazyLoadEmojis={false}
              width="100%"
            />
          </div>
        )}

        {/* Quick Message Picker */}
        {showQuickMessages && messagingWindow !== 'HUMAN_AGENT' && (
          <div
            id="quick-msg-picker"
            className="absolute bottom-16 left-3 z-50 bg-white border border-gray-200 rounded-xl shadow-lg p-3 w-80 max-w-[calc(100vw-24px)]"
          >
            <div className="mb-2 pb-2 border-b border-gray-200">
              <h3 className="text-sm font-semibold text-gray-700">Quick Messages</h3>
              <p className="text-xs text-gray-500">Click to send instantly</p>
            </div>
            <div className="space-y-1 max-h-64 overflow-y-auto">
              {quickMessages.map((msg: { id: number; text: string; icon: string }) => (
                <button
                  key={msg.id}
                  onClick={() => handleQuickMessageClick(msg.text)}
                  disabled={isSubmitting || replyClosed}
                  className="w-full text-left px-3 py-2.5 rounded-lg hover:bg-blue-50 transition-colors group disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <div className="flex items-center gap-2">
                    <span className="text-lg">{msg.icon}</span>
                    <span className="text-sm text-gray-700 group-hover:text-blue-600">{msg.text}</span>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Upload Queue */}
        {items.length > 0 && (
          <ul className="mb-3 space-y-2">
            {items.map(({ file, preview, uploading, progress, error }) => (
              <li
                key={preview}
                className="flex items-center gap-3 p-2 border border-gray-200 rounded-lg bg-gray-50/70"
              >
                <img
                  src={preview}
                  alt={file.name}
                  className="w-12 h-12 object-cover rounded-md border border-gray-200"
                />
                <div className="flex-1 min-w-0">
                  <div className="truncate text-xs font-medium text-gray-700">{file.name}</div>
                  <div className="text-[11px] text-gray-500">
                    {(file.size / 1024 / 1024).toFixed(2)} MB
                  </div>

                  {uploading && (
                    <div className="mt-1 flex items-center gap-2">
                      <div className="w-36 h-1.5 bg-gray-200 rounded">
                        <div
                          className="h-1.5 bg-blue-600 rounded transition-all"
                          style={{ width: `${progress}%` }}
                        />
                      </div>
                      <span className="text-[11px] text-gray-600 w-8">{progress}%</span>
                      <button
                        onClick={() => cancelUpload(file)}
                        className="ml-2 px-2 py-0.5 border border-red-500 text-red-600 rounded text-[11px]"
                      >
                        Cancel
                      </button>
                    </div>
                  )}
                  {error && <div className="mt-1 text-[12px] text-red-600">{error}</div>}
                </div>

                {!uploading && (
                  <button
                    onClick={() => removeItem(file)}
                    className="px-2 py-1 border border-gray-300 rounded text-xs text-gray-600 hover:bg-gray-100 transition"
                  >
                    Remove
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}

        {/* Input Row — flex wrapper keeps form + recorder side by side */}
        <div className="flex min-w-0 items-center gap-1 sm:gap-3">
          <form onSubmit={handleSubmit} className="flex min-w-0 flex-1 items-center gap-1 sm:gap-3">
            {/* Quick Message Button */}
            <button
              id="quick-msg-btn"
              type="button"
              disabled={replyClosed || messagingWindow === 'HUMAN_AGENT'}
              onClick={() => {
                setShowQuickMessages((prev) => !prev);
                setShowEmojiPicker(false);
              }}
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-gray-500 hover:bg-gray-100 transition-colors sm:h-9 sm:w-9"
              title={messagingWindow === 'HUMAN_AGENT' ? 'Type a personal reply in the human agent window' : 'Quick messages'}
            >
              <MessageSquare className="w-5 h-5" />
            </button>

            {/* Emoji Button */}
            <button
              id="emoji-btn"
              type="button"
              disabled={replyClosed}
              onClick={() => {
                setShowEmojiPicker((prev) => !prev);
                setShowQuickMessages(false);
              }}
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-gray-500 hover:bg-gray-100 transition-colors sm:h-9 sm:w-9"
              title="Emoji"
            >
              <Smile className="w-5 h-5" />
            </button>

            {/* Message Input */}
            <div className="relative min-w-0 flex-1">
              <input
                ref={inputRef}
                type="text"
                className="w-full rounded-full bg-gray-100 text-base sm:text-sm px-3 sm:px-4 py-2 sm:py-2.5 pr-8 sm:pr-10 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white placeholder-gray-500 transition"
                placeholder="Type a message..."
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                disabled={isSubmitting || replyClosed}
              />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={replyClosed}
                className="absolute right-1.5 sm:right-2.5 top-1/2 -translate-y-1/2 p-1 rounded-full text-gray-500 hover:bg-gray-200 transition"
              >
                <Paperclip className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
              </button>
            </div>

            {/* Send — hidden while recorder is active to prevent overlap on iPhone */}
            {!recorderControls.isRecording && (
              <button
                type="submit"
                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-blue-500 text-white hover:bg-blue-600 transition disabled:opacity-50 disabled:cursor-not-allowed sm:h-9 sm:w-9"
                disabled={isSubmitting || replyClosed || !message.trim()}
              >
                {isSubmitting ? (
                  <div className="w-4 h-4 sm:w-5 sm:h-5 animate-spin border-2 border-white border-t-transparent rounded-full" />
                ) : (
                  <Send className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                )}
              </button>
            )}
          </form>

          {/* Voice Message — outside <form> so its buttons never submit the form on iOS */}
          {!replyClosed && <div className="chat-audio-recorder shrink-0">
            <AudioRecorder
              onRecordingComplete={(blob) => void sendVoiceBlob(blob)}
              recorderControls={guardedRecorderControls}
              downloadOnSavePress={false}
              showVisualizer={false}
              classes={{
                AudioRecorderClass: 'chat-recorder',
                AudioRecorderStartSaveClass: 'chat-recorder-primary',
                AudioRecorderTimerClass: 'chat-recorder-timer',
                AudioRecorderStatusClass: 'chat-recorder-status',
                AudioRecorderPauseResumeClass: 'chat-recorder-pause',
                AudioRecorderDiscardClass: 'chat-recorder-discard',
              }}
            />
          </div>}
        </div>
        <input
          type="file"
          // Specific types prompt iOS Safari to transcode HEIC to JPEG automatically
          accept="image/jpeg,image/png,image/webp,image/gif"
          multiple
          className="hidden"
          ref={fileInputRef}
          onChange={handleFileSelect}
        />
      </div>
    </>
  );
}
